import { Injectable } from '@angular/core';
import { COUNTRY, LANGUAGE, USER_TYPE } from '@app/shared/types/enums';
import { BehaviorSubject, Observable } from 'rxjs';
import { IClearContext } from './interface/iclear-context.interface';
import { Business, BusinessUser } from '@app/shared/models/user.model';

@Injectable({
  providedIn: 'root',
})
export class AppContext implements IClearContext {
  /**
   * Handles the logic of making the search engine available in the toolbar depends on the view.
   */
  private readonly _showSearchBarSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly showSearchBar$ = this._showSearchBarSubject$.asObservable();

  /**
   * Handles the language of the app. If you need to watch changes you can subscribe to the observable
   */
  private readonly _languageSubject$: BehaviorSubject<LANGUAGE> = new BehaviorSubject<LANGUAGE>('ES');
  public readonly language$ = this._languageSubject$.asObservable();

  /**
   * Handles the selected country of the app. If you need to watch changes you can subscribe to the observable
   */
  private readonly _countrySubject$: BehaviorSubject<COUNTRY> = new BehaviorSubject<COUNTRY>(COUNTRY.CO);
  public readonly countrySubject$ = this._countrySubject$.asObservable();

  private readonly _userTypeSubject$: BehaviorSubject<USER_TYPE> = new BehaviorSubject<USER_TYPE>(USER_TYPE.PN);
  public readonly userType$ = this._userTypeSubject$.asObservable();

  private readonly _searchBySubject$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public readonly searchBy$ = this._searchBySubject$.asObservable();

  private readonly _searchValueSubject$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public readonly searchValue$ = this._searchValueSubject$.asObservable();

  private readonly _sessionTraceSubject$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public readonly sessionTraceId$ = this._sessionTraceSubject$.asObservable();

  private readonly _userAgentEmailSubject$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public readonly userAgentEmail$ = this._userAgentEmailSubject$.asObservable();

  private readonly _businessUserInfoSubject$: BehaviorSubject<BusinessUser | null> = new BehaviorSubject<BusinessUser | null>(null);
  public readonly businnesUserInfo$ = this._businessUserInfoSubject$.asObservable();

  private readonly _businessInfoSubject$: BehaviorSubject<Business | null> = new BehaviorSubject<Business | null>(null);
  public readonly businnesInfo$ = this._businessInfoSubject$.asObservable();

  private _useAppContextCountry = true;

  /**
   * Handles the selected country of the app. If you need to watch changes you can subscribe to the observable
   */
  private readonly _onBackgroundChange$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public readonly onBbackgroundChange$ = this._onBackgroundChange$.asObservable();
  backgroundChange(className: string): void {
    this._onBackgroundChange$.next(className);
  }

  constructor() {}

  showSearchBar(show: boolean) {
    this._showSearchBarSubject$.next(show);
  }

  setLanguage(language: LANGUAGE) {
    this._languageSubject$.next(language);
  }

  getLanguage(): LANGUAGE {
    return this._languageSubject$.getValue();
  }

  setCountry(country: COUNTRY) {
    this._countrySubject$.next(country);
  }

  getCountry(): COUNTRY {
    return this._countrySubject$.getValue();
  }

  setUserType(userType: USER_TYPE) {
    this._userTypeSubject$.next(userType);
  }

  setSearchBy(searchBy: string) {
    this._searchBySubject$.next(searchBy);
  }

  setSearchValue(searchValue: string) {
    this._searchValueSubject$.next(searchValue);
  }

  getUserType(): USER_TYPE {
    return this._userTypeSubject$.getValue();
  }

  setTraceSessionId(trace: string) {
    this._sessionTraceSubject$.next(trace);
  }

  getTraceSessionId(): string {
    return this._sessionTraceSubject$.getValue();
  }

  setUserAgentEmail(email: string) {
    this._userAgentEmailSubject$.next(email);
  }

  getUserAgentEmail(): string {
    return this._userAgentEmailSubject$.getValue();
  }

  setUseAppContextCountry(value: boolean) {
    this._useAppContextCountry = value;
  }

  getUseAppContextCountry(): boolean {
    return this._useAppContextCountry;
  }

  setBusinessUserInfo(businessUserInfo: BusinessUser) {
    this._businessUserInfoSubject$.next(businessUserInfo);
  }

  setBusinessInfo(businessInfo: Business) {
    this._businessInfoSubject$.next(businessInfo);
  }

  getBusinessUserInfo(): BusinessUser | null {
    return this._businessUserInfoSubject$.getValue();
  }

  getBusinessInfo(): Business | null {
    return this._businessInfoSubject$.getValue();
  }

  clear(): void {
    this._countrySubject$.next(COUNTRY.CO);
    this._showSearchBarSubject$.next(true);
    this._languageSubject$.next('ES');
    this._userTypeSubject$.next(USER_TYPE.PN);
    this._businessInfoSubject$.next(null);
    this._businessUserInfoSubject$.next(null);
    this.backgroundChange('');
  }
}
